import { z } from 'zod';
import {
  callEdgeFunctionValidated,
  EdgeFunctionClientError,
} from '@/lib/edge-function-client';
import { supabase } from '@/integrations/supabase/client';
import type { BDDashboardData } from '@/hooks/useBusinessDev.types';
import type { ChatResponse, SendMessageRequest } from '@/types/chat';

const bdDaySchema = z.object({
  date: z.string(),
  count: z.number(),
});

const bdRecentBidSchema = z.object({
  id: z.string(),
  job_title: z.string().nullable(),
  job_url: z.string(),
  budget_text: z.string().nullable(),
  job_type: z.string().nullable(),
  client_rating: z.number().nullable(),
  status: z.string(),
  captured_at: z.string(),
  submitted_at: z.string().nullable().optional(),
  submitted_by: z.string(),
  submitted_by_name: z.string(),
  match_score: z.number().nullable().optional(),
});

const bdDashboardDataSchema = z.object({
  today: z.object({
    count: z.number(),
    target: z.number(),
    percent_complete: z.number(),
  }),
  this_week: z.object({
    count: z.number(),
    target: z.number(),
    by_day: z.array(bdDaySchema),
  }),
  this_month: z.object({
    total: z.number(),
    captured: z.number(),
    saved: z.number().optional(),
    submitted: z.number(),
    interviewed: z.number(),
    won: z.number(),
    lost: z.number(),
    no_response: z.number(),
    win_rate: z.number(),
  }),
  rate_compliance: z.object({
    compliant_count: z.number(),
    total_count: z.number(),
    compliance_percent: z.number(),
    avg_hourly_rate: z.number(),
    minimum_rate: z.number(),
  }),
  recent_bids: z.array(bdRecentBidSchema),
  team_leaderboard: z.array(
    z.object({
      user_id: z.string(),
      full_name: z.string(),
      today_count: z.number(),
      week_count: z.number(),
      month_count: z.number(),
      connects_daily_avg: z.number().optional(),
    }),
  ),
  connects: z
    .object({
      yesterday: z.number(),
      period_total: z.number(),
      period_days: z.number(),
      daily_average: z.number(),
      boosted_count: z.number(),
      standard_count: z.number(),
      boost_bid_total: z.number(),
      boost_bid_average: z.number(),
    })
    .optional(),
  targets: z.object({
    daily: z.number(),
    weekly: z.number(),
    minimum_hourly_rate: z.number().nullable(),
    target_win_rate: z.number().nullable(),
  }),
  analytics: z
    .object({
      location_breakdown: z.array(
        z.object({
          location: z.string(),
          count: z.number(),
          won: z.number(),
          win_rate: z.number(),
        }),
      ),
      job_type_breakdown: z.array(
        z.object({
          type: z.string(),
          count: z.number(),
          won: z.number(),
          avg_rate: z.number(),
        }),
      ),
      top_skills: z.array(
        z.object({
          skill: z.string(),
          count: z.number(),
          won: z.number(),
        }),
      ),
      pipeline_funnel: z.object({
        captured: z.number(),
        submitted: z.number(),
        viewed: z.number(),
        interviewed: z.number(),
        won: z.number(),
      }),
      conversion_rates: z.object({
        capture_to_submit: z.number(),
        submit_to_viewed: z.number(),
        viewed_to_interview: z.number(),
        interview_to_win: z.number(),
        overall: z.number(),
      }),
    })
    .optional(),
});

const bdDashboardEnvelopeSchema = z.object({
  success: z.boolean(),
  data: z.unknown().optional(),
  error: z
    .object({
      message: z.string().optional(),
    })
    .optional(),
});

const askPocoResponseSchema: z.ZodType<ChatResponse> = z.object({
  chatId: z.string(),
  messageId: z.string(),
  response: z.string(),
  title: z.string().optional(),
  metadata: z.object({
    model: z.string(),
    tokenCount: z.number().optional(),
    responseTimeMs: z.number(),
    sourcesUsed: z.array(z.string()),
  }),
});

const clickupWebhookStatusSchema = z
  .object({
    suspended_webhooks: z.number().optional(),
    suspended_details: z
      .array(
        z.object({
          webhook_id: z.string().optional(),
          workspace_id: z.string().optional(),
          fail_count: z.number().nullable().optional(),
          date_suspended: z.string().nullable().optional(),
        }),
      )
      .optional(),
  })
  .passthrough();

export type ClickUpWebhookStatus = z.infer<typeof clickupWebhookStatusSchema>;

export async function fetchBDDashboard(): Promise<BDDashboardData> {
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) {
    throw new EdgeFunctionClientError('Not authenticated', {
      functionPath: 'bd-dashboard',
    });
  }

  const response = await supabase.functions.invoke('bd-dashboard', {
    method: 'GET',
  });
  if (response.error) {
    throw new EdgeFunctionClientError(response.error.message || 'Failed to fetch dashboard', {
      functionPath: 'bd-dashboard',
    });
  }
  const payload = response.data;

  const asEnvelope = bdDashboardEnvelopeSchema.safeParse(payload);
  if (asEnvelope.success) {
    if (!asEnvelope.data.success) {
      throw new EdgeFunctionClientError(
        asEnvelope.data.error?.message || 'Failed to fetch dashboard',
        {
          functionPath: 'bd-dashboard',
          responseBody: payload,
        },
      );
    }

    const parsedData = bdDashboardDataSchema.safeParse(asEnvelope.data.data);
    if (!parsedData.success) {
      throw new EdgeFunctionClientError('Invalid dashboard data payload', {
        functionPath: 'bd-dashboard',
        code: 'EDGE_FUNCTION_SCHEMA_VALIDATION_FAILED',
        details: parsedData.error.message,
        responseBody: payload,
      });
    }
    return parsedData.data;
  }

  const legacyParsed = bdDashboardDataSchema.safeParse(payload);
  if (!legacyParsed.success) {
    throw new EdgeFunctionClientError('Invalid dashboard response payload', {
      functionPath: 'bd-dashboard',
      code: 'EDGE_FUNCTION_SCHEMA_VALIDATION_FAILED',
      details: legacyParsed.error.message,
      responseBody: payload,
    });
  }

  return legacyParsed.data;
}

export async function askPoco(request: SendMessageRequest): Promise<ChatResponse> {
  return callEdgeFunctionValidated('ask-poco', askPocoResponseSchema, {
    body: {
      clientId: request.clientId,
      message: request.message,
      chatId: request.chatId,
      scope: request.scope ?? (request.clientId ? 'client' : 'global'),
    },
  });
}

export async function fetchClickUpWebhookStatus(): Promise<ClickUpWebhookStatus | null> {
  try {
    const response = await supabase.functions.invoke('clickup-webhook-manager/status');
    if (response.error || !response.data) {
      return null;
    }
    const parsed = clickupWebhookStatusSchema.safeParse(response.data);
    return parsed.success ? parsed.data : null;
  } catch (error) {
    if (error instanceof EdgeFunctionClientError) {
      return null;
    }
    throw error;
  }
}
